/**
 * Supabase sends the browser back with `#error=...` (or `?error=...`) when an invite or sign-in link
 * cannot be used: expired, already used, or replaced by a newer link. This module reads that error once
 * at startup, before the Supabase client looks at the URL, and removes it from the address bar.
 */

export interface AuthRedirectError {
  code: string;
  /** A message for people. */
  message: string;
}

const LINK_EXPIRED =
  'This link has expired or was already used. Links work once and expire after 24 hours. ' +
  'Ask your admin for a new invite link, or request a new sign-in link below.';

function readParams(raw: string): URLSearchParams {
  return new URLSearchParams(raw.replace(/^[#?]/, ''));
}

export function parseAuthRedirectError(hash: string, search: string): AuthRedirectError | null {
  for (const params of [readParams(hash), readParams(search)]) {
    const error = params.get('error');
    if (!error) continue;
    const code = params.get('error_code') ?? error;
    if (code === 'otp_expired' || error === 'access_denied') return { code, message: LINK_EXPIRED };
    return {
      code,
      message: params.get('error_description') ?? "That link couldn't be used. Try again.",
    };
  }
  return null;
}

let captured: AuthRedirectError | null | undefined;

/** Reads and clears the redirect error from the current URL (first call only). */
export function captureAuthRedirectError(): AuthRedirectError | null {
  if (captured !== undefined) return captured;
  captured = parseAuthRedirectError(window.location.hash, window.location.search);
  if (captured) {
    const url = new URL(window.location.href);
    url.hash = '';
    for (const key of ['error', 'error_code', 'error_description']) url.searchParams.delete(key);
    window.history.replaceState(window.history.state, '', url.toString());
  }
  return captured;
}
